import $ from "jquery";
import type TileManager from "./tileManager";
import { getLetterPoints } from "../shared/letterPoints";

function normalizeLetter(letter: string | null | undefined): string | null {
  if (!letter) {
    return null;
  }

  const trimmed = letter.trim().toUpperCase();
  return /^[A-Z]$/.test(trimmed) ? trimmed : null;
}

export default class LettersTile {
  private readonly id: string;
  private readonly $tile: any;
  private readonly $face: any;
  private readonly $character: any;
  private readonly $value: any;
  private readonly $gloss: any;
  private readonly $base: any;
  private readonly motionQuery: MediaQueryList | null;

  private static readonly DEBUG_ENABLED = false;

  private readonly handleMotionChange = (event: MediaQueryListEvent) => {
    this.prefersReducedMotion = event.matches;
  };
  private prefersReducedMotion: boolean;
  private currentLetter: string;
  private readonly isEmptyFlag: boolean;
  private pulseTimeout: number | null;
  private hovered = false;
  private focused = false;
  private active = false;

  constructor(
    manager: TileManager,
    id: string,
    initialLetter: string,
    isEmpty = false,
  ) {
    this.id = id;
    this.isEmptyFlag = !!isEmpty;

    const $container = manager.getContainer();
    if (!$container.length) {
      throw new Error("LettersTile: container element not found.");
    }

    this.$tile = $(
      '<button type="button" class="letters-tile" data-letter-tile aria-live="polite"></button>',
    );
    if (id) {
      this.$tile.attr("id", id);
      this.$tile.attr("data-letter-id", id);
    }

    this.$face = $(
      '<span class="letters-tile__face" data-letter-character></span>',
    );
    this.$character = $(
      '<span class="letters-tile__character" aria-hidden="true"></span>',
    );
    this.$value = $(
      '<span class="letters-tile__value" aria-hidden="true"></span>',
    );
    this.$face.append(this.$character, this.$value);
    this.$gloss = $('<span class="letters-tile__gloss"></span>');
    this.$base = $('<span class="letters-tile__base"></span>');
    this.$tile.append(this.$face, this.$gloss, this.$base);
    $container.append(this.$tile);

    this.motionQuery =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : null;
    this.prefersReducedMotion = this.motionQuery?.matches ?? false;
    if (this.motionQuery) {
      const handler = this.handleMotionChange;
      if (typeof this.motionQuery.addEventListener === "function") {
        this.motionQuery.addEventListener("change", handler);
      } else if (typeof this.motionQuery.addListener === "function") {
        this.motionQuery.addListener(handler);
      }
    }

    this.currentLetter = "";
    this.pulseTimeout = null;

    this.applyEmptyState();

    const startLetter =
      normalizeLetter(initialLetter) ?? (this.isEmptyFlag ? "" : "A");
    this.updateLetter(startLetter, false);
  }

  public destroy(): void {
    if (this.pulseTimeout !== null) window.clearTimeout(this.pulseTimeout);
    if (this.motionQuery?.removeEventListener)
      this.motionQuery.removeEventListener("change", this.handleMotionChange);
    else this.motionQuery?.removeListener(this.handleMotionChange);
    this.$tile.remove();
  }

  public getId(): string {
    return this.id;
  }

  public getElement(): HTMLButtonElement {
    return this.$tile[0];
  }

  public getBoundingClientRect(): DOMRect {
    return this.$tile[0].getBoundingClientRect();
  }

  public getLetter(): string {
    return this.currentLetter;
  }

  public isEmptyTile(): boolean {
    return this.isEmptyFlag;
  }

  public updateLetter(letter: string, animate = true): void {
    const normalized = normalizeLetter(letter);
    this.debug("updateLetter", { letter, normalized, animate });

    if (!normalized) {
      this.currentLetter = "";
      this.$tile.removeAttr("data-letter-points");
      this.$tile.removeAttr("data-letter-value");
      this.$value.text("");
      this.$tile.removeClass("letters-tile--has-value");
      this.$character.text("");
      this.$face.attr("data-letter", "");
      if (this.isEmptyFlag) {
        this.$tile.attr("data-letter", "");
        this.$tile.attr("aria-label", "Empty letter tile");
      } else {
        this.$tile.attr("data-letter", "");
        this.$tile.attr("aria-label", "Shuffle letter tile (empty)");
      }
      return;
    }

    if (normalized === this.currentLetter && !animate) {
      return;
    }

    this.currentLetter = normalized;
    this.$tile.attr("data-letter", normalized);
    this.$tile.attr("data-letter-value", normalized);

    const letterPoints = getLetterPoints(normalized);
    if (typeof letterPoints === "number" && !this.isEmptyFlag) {
      this.$tile.attr("data-letter-points", String(letterPoints));
      this.$value.text(String(letterPoints));
      this.$tile.addClass("letters-tile--has-value");
    } else {
      this.$tile.removeAttr("data-letter-points");
      this.$value.text("");
      this.$tile.removeClass("letters-tile--has-value");
    }

    const ariaLabel = this.isEmptyFlag
      ? "Empty letter tile"
      : letterPoints === undefined
        ? `Shuffle letter tile (showing ${normalized})`
        : `Shuffle letter tile (showing ${normalized}, worth ${letterPoints} point${letterPoints === 1 ? "" : "s"})`;
    this.$tile.attr("aria-label", ariaLabel);

    if (this.isEmptyFlag) {
      return;
    }

    this.$character.text(normalized);
    this.$face.attr("data-letter", normalized);

    if (animate) {
      this.animateTile();
    }
  }

  public setHovered(hover: boolean): void {
    if (this.isEmptyFlag) {
      return;
    }
    if (this.hovered === hover) {
      return;
    }
    this.hovered = hover;
    this.$tile.toggleClass("letters-tile--hover", hover);
  }

  public setFocused(focus: boolean): void {
    if (this.isEmptyFlag) {
      return;
    }
    if (this.focused === focus) {
      return;
    }
    this.focused = focus;
    this.$tile.toggleClass("letters-tile--focus", focus);
  }

  public setActive(active: boolean): void {
    if (this.isEmptyFlag) {
      return;
    }
    if (this.active === active) {
      return;
    }
    this.active = active;
    this.$tile.toggleClass("letters-tile--active", active);
    if (!active) {
      this.$tile.removeClass("letters-tile--pulse");
    }
  }

  public setDragging(dragging: boolean): void {
    if (this.isEmptyFlag) {
      return;
    }
    this.$tile.toggleClass("letters-tile--dragging", dragging);
  }

  public setDropTargetState(active: boolean): void {
    if (!this.isEmptyFlag) {
      return;
    }
    this.$tile.toggleClass("letters-tile--drop-target", active);
  }

  public setTransform(transform: string): void {
    this.$tile.css("transform", transform);
  }

  public resetTransform(): void {
    this.$tile.css("transform", "");
  }

  public setZIndex(value: number | null): void {
    if (value === null) {
      this.$tile.css("z-index", "");
    } else {
      this.$tile.css("z-index", String(value));
    }
  }

  private animateTile() {
    if (this.prefersReducedMotion) {
      return;
    }

    if (this.pulseTimeout) {
      window.clearTimeout(this.pulseTimeout);
      this.pulseTimeout = null;
    }

    this.$tile.removeClass("letters-tile--flipping letters-tile--pulse");
    void this.$tile.get(0)?.offsetWidth;

    this.$tile.addClass("letters-tile--flipping");
    this.$tile.one("animationend", () => {
      this.$tile.removeClass("letters-tile--flipping");
      this.$tile.addClass("letters-tile--pulse");
      this.pulseTimeout = window.setTimeout(() => {
        this.$tile.removeClass("letters-tile--pulse");
        this.pulseTimeout = null;
      }, 300);
    });
  }

  private applyEmptyState() {
    if (this.isEmptyFlag) {
      this.$tile.addClass("letters-tile--empty");
      this.$tile.prop("disabled", true);
      this.$tile.attr("aria-disabled", "true");
      this.$tile.attr("tabindex", "-1");
      this.$face.attr("aria-hidden", "true");
      this.$gloss.attr("aria-hidden", "true");
      this.$base.attr("aria-hidden", "true");
      this.$character.text("");
      this.$value.text("");
      this.$tile.removeClass("letters-tile--has-value");
      this.$face.attr("data-letter", "");
      this.$tile.removeAttr("data-letter-points");
    } else {
      this.$tile.removeClass("letters-tile--empty");
      this.$tile.prop("disabled", false);
      this.$tile.removeAttr("aria-disabled");
      this.$tile.removeAttr("tabindex");
      this.$face.removeAttr("aria-hidden");
      this.$gloss.removeAttr("aria-hidden");
      this.$base.removeAttr("aria-hidden");
    }
  }

  private debug(...args: unknown[]): void {
    if (!LettersTile.DEBUG_ENABLED) {
      return;
    }

    try {
      console.debug("[LettersTile]", ...args);
    } catch (error) {
      // ignore logging errors
    }
  }
}
